/* eslint-disable max-lines */
import { Box, Grid, Typography } from '@material-ui/core';
import React, { useEffect, useRef } from 'react';
import { useField } from 'formik';
import {
  AuthorityRent,
  DollarTypeFee,
} from '../../../lib/Redux/Types/ownershipForm/OwnershipFormResponse.data';

import RenderInput from '../RenderInputField/RenderInputField';
import RenderSelect from '../RenderSelect/RenderSelect';
import { useStyles } from './RenderFeeStyles';

interface RenderFeeProps {
  fieldName: string;
  label: string;
  disabled?: boolean;
  showError?: boolean;
  defaultGst: number;
  defaultPercentFee: number;
  validate?: (value: any) => void;
  defaultCalculatedValue?: string;
  defaultDollerAmount?: number;
  defaultGstAmount?: number;
  defaultFeeType?: string;
  calculatedBasedOnYearRent?: boolean;
  percentLabel?: string;
  defaultWeeksOfRent?: number;
}

// Amounts are typed by hand, often with commas: parseFloat('1,200') is 1.
export const toNumber = (value?: string | number | null): number =>
  value === undefined || value === null ? NaN : parseFloat(String(value).replace(/[^0-9.-]/g, ''));

// Same 365-day year as the percent-of-annual-rent calculation below.
const WEEKS_PER_YEAR = 365 / 7;

export const weeklyRent = (amount: number, frequency?: string | null): number => {
  switch (frequency) {
    case 'Weekly':
      return amount;
    case 'Fortnightly':
      return amount / 2;
    case 'Monthly':
      return (amount * 12) / WEEKS_PER_YEAR;
    case 'Annually':
      return amount / WEEKS_PER_YEAR;
    default:
      return NaN;
  }
};

// Keeps "1.5 weeks rental including GST" in step with the weeks; any other wording is left alone.
export const describeWeeks = (description: string | null | undefined, weeks: number): string => {
  const words = `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  if (!description || !description.trim()) return `${words} rental`;
  return description.replace(/^\s*\d+(?:\.\d+)?\s*weeks?(?![\w'])/i, words);
};

const RenderFee: React.FC<RenderFeeProps> = ({
  fieldName,
  disabled,
  label,
  defaultGst,
  defaultPercentFee,
  showError,
  validate,
  defaultCalculatedValue,
  defaultDollerAmount,
  defaultGstAmount,
  defaultFeeType,
  calculatedBasedOnYearRent,
  percentLabel,
  defaultWeeksOfRent,
}: RenderFeeProps) => {
  const [rent] = useField<AuthorityRent>('rent');
  const [feeField, feeMeta, feeHelpers] = useField<DollarTypeFee>(fieldName);
  const classes = useStyles();
  const userTouched = !!feeMeta?.touched;
  const fee: DollarTypeFee = feeField.value || {};

  // Either/or: a percent of rent, or (for a 'calculated' fee) a number of weeks of rent.
  const weeksAllowed = fee.feeType === 'calculated' && !fee.percentFee;
  const weeks = weeksAllowed ? toNumber(fee.weeks) : NaN;
  const weeksMode = weeks > 0;
  const percent = toNumber(fee.percentFee);
  const percentMode = !weeksMode && percent > 0;
  const showWeeks = weeksAllowed && (defaultWeeksOfRent !== undefined || !!fee.weeks);
  const lastAmountInputs = useRef<string | null>(null);
  const lastDollarAmount = useRef<string | null>(null);

  // Require customCalculation when percentFee is empty/removed
  const validateCustomCalculation = (value: any) => {
    const hasPercent = !!feeField?.value?.percentFee;
    const isEmpty = value === undefined || value === null || value === '';
    if (!hasPercent && isEmpty) {
      return 'Required';
    }
    return undefined;
  };

  // Require feeType when percentFee is empty/removed
  const validateFeeType = (value: any) => {
    const hasPercent = !!feeField?.value?.percentFee;
    const isEmpty = value === undefined || value === null || value === '';
    if (!hasPercent && isEmpty) {
      return 'Required';
    }
    return undefined;
  };

  const calculateDollarAmount = (
    rentAmount: string,
    percentFee: string,
    rentFrequency?: string,
  ): number => {
    if (calculatedBasedOnYearRent && rentFrequency) {
      let annualAmount = rentAmount;
      switch (rentFrequency) {
        case 'Weekly':
          annualAmount = ((toNumber(rentAmount) * 365) / 7).toFixed(2);
          break;
        case 'Fortnightly':
          annualAmount = ((toNumber(rentAmount) * 365) / 14).toFixed(2);
          break;
        case 'Monthly':
          annualAmount = (toNumber(rentAmount) * 12).toFixed(2);
          break;
        default:
          annualAmount = rentAmount;
          break;
      }
      return (toNumber(annualAmount) * toNumber(percentFee)) / 100.0;
    }
    return (toNumber(rentAmount) * toNumber(percentFee)) / 100.0;
  };

  useEffect(() => {
    // GST follows the dollar amount every time it changes, typed or worked out. Touched flags are no
    // guide: the first edit sets them, so later edits used to leave the GST behind.
    const amountText =
      fee.dollarAmount === undefined || fee.dollarAmount === null ? '' : `${fee.dollarAmount}`;
    const firstRun = lastDollarAmount.current === null;
    const changed = amountText !== lastDollarAmount.current;
    lastDollarAmount.current = amountText;
    if (firstRun || !changed || disabled) return;
    const amount = toNumber(amountText);
    const gst = amount > 0 ? (amount / defaultGst).toFixed(2) : null;
    const currentGst = fee.gst === undefined || fee.gst === null ? null : `${fee.gst}`;
    if (gst !== currentGst) feeHelpers.setValue({ ...fee, gst });
  }, [feeField.value?.dollarAmount]);

  useEffect(() => {
    // A percent of the rent, or weeks of it, is recalculated whenever the rent, the percent or the
    // weeks change, so an amount typed over the top stays only until one of them does. Touched flags
    // are no guide (the first edit and a failed submit both set them).
    const mode = weeksMode ? 'weeks' : percentMode ? 'percent' : '';
    const inputs = mode
      ? `${mode}|${toNumber(rent.value?.amount)}|${rent.value?.frequency}|${
          weeksMode ? weeks : percent
        }`
      : '';
    const firstRun = lastAmountInputs.current === null;
    const changed = inputs !== lastAmountInputs.current;
    lastAmountInputs.current = inputs;
    if (mode) {
      const amount = weeksMode
        ? weeklyRent(toNumber(rent.value?.amount), rent.value?.frequency) * weeks
        : calculateDollarAmount(
            rent.value?.amount || '',
            fee.percentFee || '',
            rent.value?.frequency,
          );
      if (disabled || !changed || !(amount > 0)) return;
      const dollarAmount = amount.toFixed(2);
      if (firstRun && fee.dollarAmount) {
        // A reopened authority keeps a saved weeks amount unless the rent has changed since. A percent
        // amount is only ever the percent of the rent, so a saved one that isn't gets corrected.
        const sameRent =
          toNumber(fee.rent) === toNumber(rent.value?.amount) &&
          fee.frequency === rent.value?.frequency;
        if (weeksMode ? sameRent : toNumber(fee.dollarAmount).toFixed(2) === dollarAmount) return;
      }
      feeHelpers.setValue({
        ...fee,
        ...(weeksMode
          ? { customCalculation: describeWeeks(fee.customCalculation, weeks) }
          : { feeType: null }),
        rent: rent.value?.amount || null,
        frequency: rent.value?.frequency || null,
        dollarAmount,
        gst: (toNumber(dollarAmount) / defaultGst).toFixed(2),
      });
      return;
    }

    // auto-fill calculated fields from rent only if the user hasn't manually changed fee fields
    if (!userTouched && rent && rent.value && !disabled) {
      const dollarAmount =
        rent.value?.amount && fee.percentFee
          ? calculateDollarAmount(rent.value.amount, fee.percentFee, rent.value.frequency)
          : fee.dollarAmount
          ? toNumber(fee.dollarAmount)
          : null;
      const gst = dollarAmount
        ? (dollarAmount / defaultGst).toFixed(2)
        : fee.gst
        ? toNumber(fee.gst)
        : null;

      feeHelpers.setValue({
        ...fee,
        feeType: fee.percentFee ? null : fee.feeType,
        rent: fee.feeType !== 'fixed' ? rent.value.amount || null : null,
        frequency: fee.feeType !== 'fixed' ? rent.value.frequency || null : null,
        dollarAmount: dollarAmount?.toFixed(2).toString(),
        gst: gst?.toString(),
      });
    }
  }, [
    rent.value,
    feeField.value?.percentFee,
    feeField.value?.feeType,
    feeField.value?.weeks,
    userTouched,
  ]);

  useEffect(() => {
    // Only initialize defaults if there is no existing value (avoid overwriting user data)
    if (!feeField.value || Object.keys(feeField.value).length === 0) {
      feeHelpers.setValue({
        ...feeField.value,
        feeType:
          feeField.value && feeField.value.feeType
            ? feeField.value.feeType
            : defaultFeeType && !feeField.value?.percentFee
            ? defaultFeeType
            : null,
        percentFee:
          feeField.value && feeField.value.percentFee
            ? feeField.value.percentFee
            : defaultPercentFee && !feeField.value?.customCalculation
            ? defaultPercentFee.toString()
            : null,
        dollarAmount:
          feeField.value && feeField.value.dollarAmount
            ? feeField.value.dollarAmount
            : defaultDollerAmount
            ? defaultDollerAmount.toString()
            : null,
        gst:
          feeField.value && feeField.value.gst
            ? feeField.value.gst
            : defaultGstAmount
            ? defaultGstAmount.toString()
            : null,
        customCalculation:
          feeField.value && feeField.value.customCalculation
            ? feeField.value.customCalculation
            : defaultCalculatedValue && !feeField.value?.percentFee
            ? defaultCalculatedValue
            : '',
        weeks:
          feeField.value && feeField.value.weeks
            ? feeField.value.weeks
            : defaultWeeksOfRent && !defaultPercentFee
            ? defaultWeeksOfRent.toString()
            : null,
      });
    }
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2" className={classes.subSectionHeader}>
        {label}
      </Typography>
      <Grid container className={classes.feeContainer}>
        <Grid item xs={2} className={classes.feeSubContainer2}>
          <RenderInput name={`${fieldName}.percentFee`} disabled={disabled} />
        </Grid>
        <Grid item xs={10} className={classes.feeSubContainer2}>
          <Typography className={classes.text}>
            {percentLabel || '% including GST of the Annual Rental'}
          </Typography>
        </Grid>
        <Grid item xs={12} className={classes.feeSubContainer2}>
          <Typography className={classes.orText}>
            <em>OR</em>
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Box marginBottom={2} marginTop={3}>
            <RenderSelect
              label="Select fee type"
              name={`${fieldName}.feeType`}
              disabled={disabled || !!feeField.value?.percentFee}
              options={[
                {
                  value: '',
                  label: 'Select fee type',
                },
                {
                  value: 'calculated',
                  label: 'Calculated',
                },
                {
                  value: 'fixed',
                  label: 'Fixed Fee',
                },
              ]}
              validate={validateFeeType}
            />
          </Box>
        </Grid>

        <Grid item xs={6} className={classes.feeSubContainer2}>
          <Box style={{ width: '100%' }}>
            <RenderInput
              fullWidth
              label="Calculated as follows"
              name={`${fieldName}.customCalculation`}
              // allow editing regardless of feeType (editable when fixed)
              disabled={disabled}
              validate={validateCustomCalculation}
              showError={showError}
            />
          </Box>
        </Grid>
        {showWeeks && (
          <>
            <Grid item xs={2} className={classes.feeSubContainer2}>
              <RenderInput name={`${fieldName}.weeks`} disabled={disabled} />
            </Grid>
            <Grid item xs={10} className={classes.feeSubContainer2}>
              <Typography className={classes.text}>weeks of rent</Typography>
            </Grid>
          </>
        )}
        <Grid item xs={12} className={`${classes.feeSubContainer2}${classes.orText}`}>
          <Typography>
            <strong>Dollar Amount</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={4} className={classes.feeSubContainer}>
          <RenderInput
            name={`${fieldName}.dollarAmount`}
            currencyField
            disabled={disabled}
            validate={validate}
            showError={showError}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={4} className={classes.feeSubContainer}>
          <Typography className={classes.text}>including GST of</Typography>
        </Grid>
        <Grid item xs={12} md={4} className={classes.feeSubContainer}>
          <RenderInput fullWidth name={`${fieldName}.gst`} currencyField disabled={disabled} />
        </Grid>
        {feeField.value?.feeType !== 'fixed' && (
          <>
            <Grid item xs={12}>
              <Typography className={classes.text}>if the Rent is</Typography>
            </Grid>
            <Grid item xs={12} md={5} className={classes.feeSubContainer}>
              <RenderInput fullWidth name={`${fieldName}.rent`} currencyField disabled={disabled} />
            </Grid>
            <Grid item xs={12} md={2} className={classes.feeSubContainer}>
              <Typography className={classes.text}>per</Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <RenderSelect
                label=""
                name={`${fieldName}.frequency`}
                disabled={disabled}
                defaultValue={(rent && rent.value && rent.value.frequency) || ''}
                options={[
                  {
                    value: 'Weekly',
                    label: 'Week',
                  },
                  {
                    value: 'Fortnightly',
                    label: 'Fortnight',
                  },
                  {
                    value: 'Monthly',
                    label: 'Month',
                  },
                  {
                    value: 'Annually',
                    label: 'Annum',
                  },
                ]}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default RenderFee;
